import React, { useState, useEffect } from 'react';
import { Plus, Pencil, X, Trash2, Image, PanelTop, LayoutPanelLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import ImageField from '../../components/ImageField';
import { requestPin } from '../../components/PinModal';
import { toast } from 'sonner';

const KINDS = [
  { id: 'hero', label: 'Banners Principales', icon: PanelTop },
  { id: 'promo', label: 'Bloques Promocionales', icon: LayoutPanelLeft },
];

const EMPTY = {
  kind: 'hero', internalName: '', title: '', subtitle: '', description: '',
  imageDesktop: '', imageMobile: '', imageLeft: '', imageRight: '',
  buttonText: '', buttonUrl: '/tienda', order: 1, active: true,
  ownerId: 'matriz', useCorporate: true, blockType: 'promocion',
  seoTitle: '', imageAlt: '', internalTag: '',
};

export default function AdminContenido() {
  const [kind, setKind] = useState('hero');
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null); // bloque a eliminar
  const [franchises, setFranchises] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);

  const load = () => api.getContentBlocks(null, true).then(setBlocks).catch(() => {});

  useEffect(() => {
    load();
    api.getFranchisesFresh().then(setFranchises).catch(() => {});
    api.getValueListsFresh().then((lists) => setBlockTypes(lists.find((l) => l.id === 'tipos-bloque')?.values.filter((v) => v.active) || [])).catch(() => {});
  }, []);

  const kindBlocks = blocks.filter((b) => b.kind === kind).sort((a, b) => a.order - b.order);
  const activeCount = kindBlocks.filter((b) => b.active && b.ownerId === 'matriz').length;

  const toggleActive = async (b) => {
    try {
      const updated = await api.updateContentBlock(b.id, { active: !b.active });
      setBlocks((prev) => prev.map((x) => x.id === b.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  const save = async () => {
    if (!editing.internalName.trim()) { toast.error('El nombre interno es obligatorio'); return; }
    try {
      if (editing.id) {
        const { id, ...data } = editing;
        const updated = await api.updateContentBlock(id, data);
        setBlocks((prev) => prev.map((b) => b.id === id ? updated : b));
      } else {
        const created = await api.createContentBlock({ ...editing, kind });
        setBlocks((prev) => [...prev, created]);
      }
      setEditing(null);
      toast.success('Bloque guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    const token = await requestPin(`Eliminar bloque "${deleting.internalName}"`);
    if (!token) return;
    try {
      await api.deleteContentBlock(deleting.id, token);
      setBlocks((prev) => prev.filter((b) => b.id !== deleting.id));
      setDeleting(null);
      toast.success('Bloque eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
  const field = (label, key, placeholder = '', testid = null) => (
    <div>
      <label className="block text-xs font-bold text-stone-500 mb-1">{label}</label>
      <input value={editing[key] || ''} onChange={set(key)} placeholder={placeholder} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={testid} />
    </div>
  );

  return (
    <div data-testid="admin-contenido">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Image className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Contenido Web</h1>
          </div>
          <p className="text-sm text-stone-500">La home evoluciona desde aquí: 1 elemento activo = estático · 2 o más = slider automático. Sin tocar código.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, kind, order: kindBlocks.length + 1 })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-block-button">
          <Plus className="w-4 h-4" /> Nuevo {kind === 'hero' ? 'banner' : 'bloque'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {KINDS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => { setKind(id); setEditing(null); }} className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full border transition-colors ${kind === id ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid={`kind-tab-${id}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
        <span className="ml-auto text-xs font-bold px-3 py-1.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200" data-testid="display-mode">
          Modo actual: {activeCount === 0 ? 'Oculto (0 activos)' : activeCount === 1 ? 'Estático (1 activo)' : `Slider (${activeCount} activos)`}
        </span>
      </div>

      {deleting && (
        <div className="bg-white rounded-lg border-2 border-red-300 p-6 mb-6" data-testid="delete-confirm">
          <h2 className="font-black text-stone-800 mb-2">Eliminar “{deleting.internalName}”</h2>
          <p className="text-sm text-stone-500 mb-4">Acción sensible: requiere PIN Maestro. Si solo quieres retirarlo temporalmente, usa el conmutador Activo.</p>
          <div className="flex gap-3">
            <button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 transition-colors text-white px-5 py-2.5 rounded text-sm font-bold" data-testid="confirm-delete-button">Eliminar</button>
            <button onClick={() => setDeleting(null)} className="px-4 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-8" data-testid="block-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-stone-800">{editing.id ? `Editar: ${editing.internalName}` : `Nuevo ${kind === 'hero' ? 'banner principal' : 'bloque promocional'}`}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {field('Nombre interno *', 'internalName', 'Campaña verano 2026', 'block-name-input')}
            {field('Título', 'title', '', 'block-title-input')}
            {kind === 'hero' ? field('Subtítulo', 'subtitle') : field('Descripción', 'description')}
            {kind === 'hero' ? (
              <>
                <ImageField label="Imagen desktop" value={editing.imageDesktop} onChange={(v) => setEditing({ ...editing, imageDesktop: v })} ownerId={editing.ownerId} testid="pick-image-desktop" />
                <ImageField label="Imagen móvil (opcional)" value={editing.imageMobile} onChange={(v) => setEditing({ ...editing, imageMobile: v })} ownerId={editing.ownerId} testid="pick-image-mobile" />
              </>
            ) : (
              <>
                <ImageField label="Imagen izquierda" value={editing.imageLeft} onChange={(v) => setEditing({ ...editing, imageLeft: v })} ownerId={editing.ownerId} testid="pick-image-left" />
                <ImageField label="Imagen derecha" value={editing.imageRight} onChange={(v) => setEditing({ ...editing, imageRight: v })} ownerId={editing.ownerId} testid="pick-image-right" />
              </>
            )}
            {field('Texto botón', 'buttonText')}
            {field('URL botón', 'buttonUrl', '/tienda')}
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Orden</label>
              <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 1 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="block-order-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Tipo de bloque</label>
              <select value={editing.blockType} onChange={set('blockType')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="block-type-select">
                {blockTypes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Propietario</label>
              <select value={editing.ownerId} onChange={set('ownerId')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="block-owner-select">
                {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            {editing.ownerId !== 'matriz' && (
              <div className="md:col-span-3 flex items-center gap-3 bg-sky-50 border border-sky-200 rounded px-4 py-3">
                <Switch checked={editing.useCorporate} onCheckedChange={(v) => setEditing({ ...editing, useCorporate: v })} data-testid="use-corporate-switch" />
                <div>
                  <p className="text-sm font-bold text-stone-700">Usar banner corporativo MATRIZ</p>
                  <p className="text-xs text-stone-500">Desactívalo para que esta franquicia sustituya los corporativos por los suyos propios. No afecta al resto de franquicias.</p>
                </div>
              </div>
            )}
            {field('Título SEO (opcional)', 'seoTitle')}
            {field('Texto alternativo imagen (ALT)', 'imageAlt')}
            {field('Etiqueta interna', 'internalTag', 'home-hero')}
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={save} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-block-button">Guardar</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium w-12">Orden</th>
              <th className="px-4 py-3 font-medium">Elemento</th>
              <th className="px-4 py-3 font-medium">Propietario</th>
              <th className="px-4 py-3 font-medium">Tipo</th>
              <th className="px-4 py-3 font-medium w-20">Activo</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {kindBlocks.map((b) => (
              <tr key={b.id} className="border-t border-stone-100" data-testid={`block-row-${b.id}`}>
                <td className="px-4 py-3 font-black text-stone-400 text-center">{b.order}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={b.imageDesktop || b.imageLeft || b.imageRight} alt="" className="w-16 h-10 object-cover rounded bg-stone-100" />
                    <div>
                      <p className="font-bold text-stone-800">{b.internalName}</p>
                      <p className="text-xs text-stone-400">{b.title}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={`font-bold px-2 py-1 rounded ${b.ownerId === 'matriz' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                    {b.ownerId === 'matriz' ? 'MATRIZ' : franchises.find((f) => f.id === b.ownerId)?.name || b.ownerId}
                  </span>
                  {b.ownerId !== 'matriz' && <p className="text-[10px] text-stone-400 mt-1">{b.useCorporate ? 'Usa corporativo' : 'Personalizado'}</p>}
                </td>
                <td className="px-4 py-3 text-stone-600 text-xs">{blockTypes.find((t) => t.id === b.blockType)?.label || b.blockType}</td>
                <td className="px-4 py-3"><Switch checked={b.active} onCheckedChange={() => toggleActive(b)} data-testid={`toggle-block-${b.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => { setEditing({ ...b }); setDeleting(null); }} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-block-${b.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => { setDeleting(b); setEditing(null); }} className="text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-block-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {kindBlocks.length === 0 && <p className="text-center text-stone-400 py-10">No hay elementos. Crea el primero.</p>}
      </div>
    </div>
  );
}
